import { Component, inject, OnDestroy, OnInit, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserService } from 'src/app/project/modules/user/user.service';
import { PromptService } from '../../../components/prompt/prompt.service';
import { ActiveService } from '../../../services/active/active.service';
import { ClearService } from '../../../services/clear.service';
import { WhiteLabelService } from '../../white-label/white-label.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AuthService } from '../auth.service';

import { select, Store } from '@ngrx/store';
import { EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { DeviceService } from '../../../../core/services/device.service';
import { TAccount } from '../../account/account.model';

import { SafeResourceUrl, Title } from '@angular/platform-browser';
import { TAccountSummaryResponse } from '@project/view-models';
import { blurInput } from 'src/app/core/helpers/blur-input';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { routeToUrl } from 'src/app/core/helpers/route-to-url';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { logErrorInSentry } from 'src/app/project/modules/errors/response-error';
import { HubspotService } from 'src/app/project/services/hubspot.service';
import { EAuthRoute } from 'src/app/project/shared/constants/auth.constants';
import { EStore } from 'src/app/project/shared/enums/store.enum';
import { EIconPath } from '../../../shared/enums/icons.enum';
import { ResponseErrorService } from '../../errors/response-error.service';
import { UpdateService } from '../../sync/update.service';
import { TwoFactorAuthenticationService } from '../2fa/two-factor-authentication.service';
import { TPasswordInputVisibilityType } from '../auth.types';
import { LoginRedirectService } from '../login-redirect.service';
import { LogoutService } from '../logout.service';
import { PasswordResetService } from '../password-reset/password-reset.service';
import { HttpErrorResponse } from '@angular/common/http';
import { iconMap } from 'src/app/project/shared/enums/icon-map';
import { RebrandService } from 'src/app/core/services/rebrand.service';
import { TTwoFactorAuthenticationState } from '../2fa/two-factor-authentication-state.model';
import { TwoFactorAuthenticationStateService } from '../2fa/two-factor-authentication-state.service';

@Component({
  selector: 'pp-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  rememberMe = false;
  processing = false;
  passInputType: TPasswordInputVisibilityType;
  private pageTitle = 'Pinpoint Works';
  hubspotMarketingPageUrl: SafeResourceUrl;

  isiPhone: boolean = this.deviceService.isiPhone();
  isiPod: boolean = this.deviceService.isiPod();
  isiPad: boolean = this.deviceService.isiPad();

  authForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required]),
  });

  warningIcon = iconMap[EIconPath.ICON_WARNING_TRIANGLE_ERROR];
  mailIcon = iconMap[EIconPath.ICON_MISC_MAIL_18];
  passwordIcon = iconMap[EIconPath.ICON_MISC_PASSWORD_18];

  shouldValidate = { email: false, password: false };

  private accounts$: Observable<TAccount[]>;
  private readonly destroy$ = new Subject<void>();
  EIconPath = EIconPath;
  iconMap = iconMap;
  readonly rebrandEnabled: Signal<boolean> = inject(RebrandService).rebrandEnabled;

  constructor(
    private store: Store<{
      accounts: TAccount[];
    }>,
    private router: Router,
    private authService: AuthService,
    private logoutService: LogoutService,
    private promptService: PromptService,
    private workspaceService: WorkspaceService,
    private whiteLabelService: WhiteLabelService,
    private clearService: ClearService,
    private activeService: ActiveService,
    private deviceService: DeviceService,
    private translationPipe: TranslationPipe,
    private userService: UserService,
    private titleService: Title,
    private updateService: UpdateService,
    private passwordResetService: PasswordResetService,
    private responseErrorService: ResponseErrorService,
    private twoFactorAuthenticationService: TwoFactorAuthenticationService,
    private loginRedirectService: LoginRedirectService,
    private hubspotService: HubspotService,
    private twoFactorAuthenticationStateService: TwoFactorAuthenticationStateService,
  ) {
    this.accounts$ = this.store.pipe(select(EStore.ACCOUNTS));
    this.setHubspotURL();
  }

  ngOnInit(): void {
    this.titleService.setTitle(this.pageTitle);
    this.whiteLabelService.setDefaultStyle();

    this.checkIfIsLoggingOut();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  login(): void {
    this.shouldValidate['email'] = true;
    this.shouldValidate['password'] = true;
    this.authForm.markAllAsTouched();

    this.processing = true;
    this.clearService.clearAllData();
    this.trySetRememberMeForMobiles();

    if (this.authForm.invalid) {
      this.processing = false;
      this.authForm.markAllAsTouched();
      this.authForm.markAllAsDirty();
      return;
    }

    const email = this.authForm.get('email')?.value;
    const password = this.authForm.get('password')?.value;

    this.authService
      .login(email, password, this.rememberMe)
      .pipe(
        switchMap(() => this.handleLoginSuccess()),
        catchError((error) => this.handleLoginError(error)),
        finalize(() => {
          this.processing = false;
        }),
      )
      .subscribe();
  }

  onInput(controlName: string): void {
    const control = this.authForm.get(controlName);

    this.shouldValidate[controlName] = control.valid || !control.touched;
  }

  onBlur(controlName: string): void {
    const control = this.authForm.get(controlName);

    this.shouldValidate[controlName] = control.invalid;
  }

  shouldShowError(controlName: string): boolean {
    const control = this.authForm.get(controlName);

    if (!this.shouldValidate[controlName]) {
      return false;
    }

    return control.invalid && control.touched;
  }

  onRememberMeChecked(): void {
    this.rememberMe = !this.rememberMe;
  }

  onKeyDownEnter(event: Event): void {
    blurInput(event.target);

    if (!this.processing) {
      this.login();
    }
  }

  forgotPassword(): void {
    this.passwordResetService.setEmail(this.authForm.get('email')?.value);
    this.router.navigate([EAuthRoute.RESET_PASSWORD]);
  }

  private trySetRememberMeForMobiles(): void {
    if (this.isiPhone || this.isiPod || this.isiPad) {
      this.rememberMe = true;
    }
  }

  private checkIfIsLoggingOut(): void {
    const isLoggingOut = this.logoutService.getIsLoggingOut();

    if (isLoggingOut) {
      this.logoutService.setIsLoggingOutComplete();
    } else {
      this.checkActiveAuth();
    }
  }

  private checkActiveAuth(): void {
    // TODO: need product spec for navigating to '/'; rethink if it's needed; rethink logic
    this.accounts$.pipe(takeUntil(this.destroy$)).subscribe((accounts) => {
      const accountId = this.activeService.getActiveAccountId();

      if (accountId) {
        this.router.navigate(['/']);
      }
    });

    this.userService
      .fetchUser()
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          const user = this.userService.getUser();

          // TODO: need product spec for what to do when there's no active workspace for this scenario
          if (user.activeWorkspaceId) {
            this.router.navigate(['/site', user.activeWorkspaceId]);
          }
        }),
        catchError((error) => {
          const isLoggedOut = this.router.url?.includes(EAuthRoute.LOGIN);
          if (!isLoggedOut) {
            logErrorInSentry({ error, message: 'Error fetching user' });
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private handleLoginSuccess(): Observable<TAccountSummaryResponse[]> {
    this.updateService.checkForUpdateAfterLogin();

    return this.workspaceService.fetchWorkspaces().pipe(
      tap(() => {
        this.userService
          .fetchUser()
          .pipe(
            switchMap(() => this.twoFactorAuthenticationStateService.getState$().pipe(take(1))),
            tap((twoFactorAuthentication) => {
              this.handleRedirect(twoFactorAuthentication);
            }),
          )
          .subscribe();
      }),
    );
  }

  private handleRedirect(twoFactorAuthentication: TTwoFactorAuthenticationState): void {
    const { enabled2fa, setup2faAfter } = twoFactorAuthentication;
    const isSetup2FALaterExpired = !setup2faAfter || setup2faAfter < Date.now();

    if (!enabled2fa && isSetup2FALaterExpired) {
      this.router.navigate([routeToUrl(EAuthRoute.TWO_FACTOR_AUTHENTICATION_SETUP)]);
    }

    if (!enabled2fa && !isSetup2FALaterExpired) {
      this.loginRedirectService.redirectAfterLogin();
    }
  }

  private handleLoginError(error: HttpErrorResponse): Observable<null> {
    if (error.status === EStatusCode.UNAUTHORIZED) {
      this.showErrorPrompt(error.status);
      return of(null);
    }

    if (error.status === EStatusCode.FORBIDDEN) {
      this.twoFactorAuthenticationService.handleLoginError(error);
      return of(null);
    }

    logErrorInSentry({
      error,
      message: 'Error trying to login',
      tags: {
        email: this.authForm.get('email')?.value,
      },
    });
    this.showErrorPrompt(error.status);
    this.responseErrorService.errors(error.status);

    return of(null);
  }

  public showErrorPrompt(statusCode: EStatusCode): void {
    this.promptService.showError(this.getErrorPromptText(statusCode), {
      duration: this.getErrorPromptDurationSeconds(statusCode),
      left: true,
    });
  }

  private getErrorPromptText(statusCode: EStatusCode): string {
    switch (statusCode) {
      case EStatusCode.UNAUTHORIZED:
        return this.translationPipe.transform('prompt_incorrect_credentials');
      case EStatusCode.NO_RESPONSE:
        return this.translationPipe.transform('prompt_internet_connection');
      case EStatusCode.GATEWAY_TIMEOUT:
        return this.translationPipe.transform('prompt_server_error');
      default:
        return this.translationPipe.transform('prompt_error');
    }
  }

  private getErrorPromptDurationSeconds(statusCode: EStatusCode): number {
    switch (statusCode) {
      case EStatusCode.GATEWAY_TIMEOUT:
        return 30;
      default:
        return 5;
    }
  }

  private setHubspotURL(): void {
    this.hubspotMarketingPageUrl = this.hubspotService.getHubspotURL();
  }
}
